/**
 * Route Registry (iter204) — single source of truth for:
 *   • sidebar nav (via sidebarConfig.js building on this registry)
 *   • home tiles (frontend fallback when /api/home/tiles fails)
 *   • breadcrumb "up" navigation (via parent chains)
 *
 * Every capability appears exactly once here with a stable `key`.
 * Duplicates that we deliberately keep reachable by direct URL live
 * on this registry with `hideFromNav: true` so nav filtering is a
 * one-liner while the routes remain accessible.
 */

export const LANES = { UK: 'uk', EU: 'eu', USA: 'usa' };

// ---------------------------------------------------------------------------
// Canonical entries
// ---------------------------------------------------------------------------
export const ROUTE_REGISTRY = [
  // ------------------------------ core -----------------------------------
  { key: 'home', label: 'Home', route: '/home',
    iconKey: 'Home', parent: null, groupId: 'home', enabled: true },
  // Iter236 — Dashboard removed from active nav; kept here so the
  // /dashboard compat route (redirects to /home) can still resolve a
  // breadcrumb during the redirect flash.
  { key: 'dashboard-legacy', label: 'Home', route: '/dashboard',
    iconKey: 'LayoutDashboard', parent: 'home', groupId: 'home',
    enabled: true, hideFromNav: true, alias: 'home' },

  // Group: Workflows — grouped under a single Workflows parent so
  // breadcrumbs read Workspace → Workflows → <page>.
  { key: 'workflows', label: 'Workflows', route: '/workflows',
    iconKey: 'GitBranch', parent: null, groupId: 'workflows',
    enabled: true,
    description: 'RGR, overpayment and duty repayment workflows' },
  { key: 'export-declarations', label: 'Export Declarations',
    route: '/export-declarations', iconKey: 'FileOutput',
    parent: 'workflows', groupId: 'workflows', enabled: true },

  // Group: Operations
  { key: 'work-queue', label: 'Work Queue', route: '/work-queue',
    iconKey: 'Inbox', parent: null, groupId: 'operations', enabled: true },
  { key: 'approvals-queue', label: 'Approvals', route: '/approver-queue',
    iconKey: 'CheckSquare', parent: null, groupId: 'operations',
    enabled: true },
  { key: 'review-queue', label: 'Review Queue', route: '/review-queue',
    iconKey: 'ScanSearch', parent: null, groupId: 'operations', enabled: true },
  { key: 'all-cases', label: 'All Cases', route: '/cases',
    iconKey: 'FileText', parent: null, groupId: 'operations', enabled: true },
  { key: 'case-items', label: 'Case Items', route: '/case-items',
    iconKey: 'Package', parent: null, groupId: 'operations', enabled: true, hideFromNav: true },

  // Group: Insights & Reports
  { key: 'insights', label: 'Insights', route: '/insights',
    iconKey: 'TrendingUp', parent: null, groupId: 'insights', enabled: true },
  { key: 'reports', label: 'Reports', route: '/reports',
    iconKey: 'BarChart3', parent: null, groupId: 'insights', enabled: true },

  // ---------------------- lanes / tile-level entries --------------------
  { key: 'uk.rgr_returns', label: 'RGR Returns',
    route: '/workflows?tab=rgr_missed_relief',
    iconKey: 'RotateCcw', lane: LANES.UK, parent: 'workflows',
    groupId: 'workflows', enabled: true, canonical: true,
    description: 'Returned Goods Relief: real-time + missed relief.' },
  { key: 'uk.import_review', label: 'Import Repayment Review v2',
    route: '/import-review', iconKey: 'Search',
    lane: LANES.UK, parent: 'workflows', groupId: 'workflows',
    enabled: true, canonical: true, hideFromNav: true,
    description: 'Historical import declarations + overpayment recovery.' },
  // Iter248 fix — standalone Import (staging inbox) route; kept reachable
  // for direct navigation and breadcrumbs. Registry entry mirrors the
  // <Route path="/import"> in App.js so the crumb chain reads
  // Home → Workflows → Import.
  { key: 'workflows.shipments', label: 'Shipments',
    route: '/shipments', iconKey: 'Ship',
    parent: 'workflows', groupId: 'workflows', enabled: true,
    canonical: true,
    description: 'Cross-border shipments (MRN, date, origin, duty) with line-level drilldown.' },
  { key: 'uk.hmrc_filing', label: 'HMRC Direct Filing',
    route: '/admin/cds-platform', iconKey: 'Send',
    lane: LANES.UK, parent: null, groupId: 'admin', enabled: true },
  { key: 'eu.returns', label: 'EU Returns',
    route: '/eu-returns', iconKey: 'Truck',
    lane: LANES.EU, parent: 'workflows', groupId: 'workflows',
    enabled: true,
    description: 'EU H7 €3 duty reclaim lane hub.' },
  { key: 'eu.h7_compilation', label: 'EU H7 Compilation',
    route: '/eu-h7-compilation', iconKey: 'FileCheck',
    lane: LANES.EU, parent: 'eu.returns', enabled: true,
    description: 'Compile H7 €3 reclaim packs, analytics + broker bundle export.' },
  { key: 'eu.h7_reason_mapper', label: 'EU H7 Reason Mapper',
    route: '/eu-h7-reason-mapper', iconKey: 'MessageSquare',
    lane: LANES.EU, parent: 'eu.returns', enabled: true,
    description: 'Map raw return reason strings to canonical H7 UCC codes.' },

  // USA lane — Iter S25 Phase 27.5 (Fix 3) — canonical route is now
  // /usa-returns. The former /usa-coming-soon placeholder + page +
  // Header label mapping are fully retired.
  { key: 'usa.claim_packs', label: 'USA Claim Packs',
    route: '/usa-returns', iconKey: 'FileText',
    lane: LANES.USA, parent: 'workflows', enabled: true,
    description: 'USA duty drawback claim packs.' },

  // ---------------------- admin / cross-cutting -------------------------
  { key: 'admin', label: 'System Admin', route: '/admin',
    iconKey: 'Shield', parent: null, groupId: 'admin', enabled: true },
  { key: 'admin.cds-platform', label: 'CDS Platform',
    route: '/admin/cds-platform', iconKey: 'Activity',
    parent: 'admin', groupId: 'admin', enabled: true },
  { key: 'admin.hmrc-notifications', label: 'HMRC Notifications',
    route: '/admin/hmrc-notifications', iconKey: 'Bell',
    parent: null, groupId: 'operations', enabled: true },
  { key: 'admin.prospects', label: 'Prospects', route: '/admin/prospects',
    iconKey: 'Users', parent: null, groupId: 'growth', enabled: true,
    hideFromNav: true },
  { key: 'admin.audit-log', label: 'Audit Log', route: '/audit-logs',
    iconKey: 'FileBarChart', parent: 'admin', groupId: 'admin',
    enabled: true },

  // -------------------- HIDDEN duplicates (direct-URL only) -------------
  // Kept reachable so any bookmarks/old links don't 404, hidden from
  // nav so operators only see the canonical Workflows entry. Iter255 —
  // label mirrors the canonical entry so RGR breadcrumb resolution on
  // /rgr-returns (and /import-review) matches the sidebar label.
  { key: 'legacy.rgr-returns', label: 'RGR Returns',
    route: '/rgr-returns', iconKey: 'RotateCcw',
    parent: 'uk.rgr_returns', enabled: true,
    hideFromNav: true, alias: 'uk.rgr_returns' },
  { key: 'legacy.import-review-list', label: 'Import Repayment Review v2',
    route: '/import-review', iconKey: 'FileSpreadsheet',
    parent: 'uk.import_review', enabled: true,
    hideFromNav: true, alias: 'uk.import_review' },
];


// ---------------------------------------------------------------------------
// Lookups
// ---------------------------------------------------------------------------
const BY_KEY = Object.freeze(
  ROUTE_REGISTRY.reduce((acc, e) => { acc[e.key] = e; return acc; }, {}),
);
const BY_ROUTE = Object.freeze(
  ROUTE_REGISTRY.reduce((acc, e) => {
    // Only the first canonical entry wins so aliases don't override.
    if (!acc[e.route] || e.canonical) acc[e.route] = e;
    return acc;
  }, {}),
);

export function getByKey(key) { return BY_KEY[key] || null; }
export function getByRoute(route) {
  if (BY_ROUTE[route]) return BY_ROUTE[route];
  // Fallback: match by pathname prefix so /cases/abc still resolves.
  const [path] = (route || '').split('?');
  return ROUTE_REGISTRY.find((e) => path === e.route
    || path?.startsWith(`${e.route}/`)) || null;
}

/** Resolve the parent-chain up to root. Used for breadcrumbs. */
export function getBreadcrumbTrail(routeOrKey) {
  const start = typeof routeOrKey === 'string'
    ? (BY_KEY[routeOrKey] || getByRoute(routeOrKey))
    : routeOrKey;
  if (!start) return [];
  const chain = [start];
  let cur = start;
  while (cur && cur.parent) {
    cur = BY_KEY[cur.parent];
    if (!cur) break;
    chain.unshift(cur);
  }
  return chain;
}

/** All entries visible in the sidebar for a given role. */
export function navEntriesForRole(role) {
  return ROUTE_REGISTRY.filter((e) => {
    if (!e.enabled) return false;
    if (e.hideFromNav) return false;
    if (e.roles && role && !e.roles.includes(role)) return false;
    return true;
  });
}

export default ROUTE_REGISTRY;
